// ─────────────────────────────────────────────────────────────────────────────
// Session utilisateur — source de vérité unique de l'authentification.
//
//   token présent au démarrage ─► "checking" ─► GET /auth/verify
//        ├─ session valide          ─► "authenticated" (rôle + dépôt du serveur)
//        ├─ 401 / session invalide  ─► "anonymous"     (token supprimé)
//        └─ réseau / erreur 5xx     ─► "unreachable"   (token CONSERVÉ, réessayer)
//   pas de token ─► "anonymous"
// ─────────────────────────────────────────────────────────────────────────────
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AppDispatch } from "../../app/store";
import { ApiError } from "../../shared/api/http";
import { session } from "../../shared/api/session";
import { SessionInfo, verifySession } from "./api";
import { BiUser } from "./types";

export type AuthStatus = "checking" | "authenticated" | "anonymous" | "unreachable";

interface AuthState {
  status: AuthStatus;
  /** Une vérification est en cours (évite les appels concurrents). */
  verifying: boolean;
  user: BiUser | null;
  /** Rôle tel que renvoyé par le serveur ("" si inconnu). */
  role: string;
  depot: string;
}

const initialState: AuthState = {
  status: session.getToken() ? "checking" : "anonymous",
  verifying: false,
  user: session.getUser<BiUser>(),
  role: "",
  depot: "",
};

type RestoreError = "unauthorized" | "unreachable";

/** Vérifie le token stocké auprès du backend (démarrage, après login, « Réessayer »). */
export const restoreSession = createAsyncThunk<
  SessionInfo,
  void,
  { rejectValue: RestoreError; state: { auth: AuthState } }
>(
  "auth/restoreSession",
  async (_, { rejectWithValue }) => {
    if (!session.getToken()) return rejectWithValue("unauthorized");
    try {
      const info = await verifySession();
      if (!info.valid) {
        session.clear();
        return rejectWithValue("unauthorized");
      }
      return info;
    } catch (err) {
      // Serveur injoignable ou en erreur : on garde la session, l'utilisateur pourra réessayer
      if (err instanceof ApiError && (err.isNetworkError || err.status >= 500)) {
        return rejectWithValue("unreachable");
      }
      session.clear();
      return rejectWithValue("unauthorized");
    }
  },
  { condition: (_, { getState }) => !getState().auth.verifying },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loggedOut(state) {
      state.status = "anonymous";
      state.user = null;
      state.role = "";
      state.depot = "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(restoreSession.pending, (state) => {
        state.status = "checking";
        state.verifying = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.status = "authenticated";
        state.verifying = false;
        state.role = action.payload.role;
        state.depot = action.payload.depot;
        state.user = session.getUser<BiUser>();
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.verifying = false;
        if (action.payload === "unreachable") {
          state.status = "unreachable";
          return;
        }
        state.status = "anonymous";
        state.user = null;
        state.role = "";
        state.depot = "";
      });
  },
});

/** Déconnexion : vide le stockage puis l'état (effet de bord hors reducer). */
export const logout = () => (dispatch: AppDispatch) => {
  session.clear();
  dispatch(authSlice.actions.loggedOut());
};

export default authSlice.reducer;
